/**
 * GHL (Go High Level) API Service
 *
 * Handles all API interactions with GHL for custom field management.
 */

import axios from 'axios';
import logger from '../utils/logger';

// GHL API Base URL
const GHL_API_BASE = 'https://services.leadconnectorhq.com';

// Create axios instance with default config
const ghlApi = axios.create({
  baseURL: GHL_API_BASE,
  headers: {
    'Content-Type': 'application/json',
    'Version': '2021-07-28',
  },
});

/**
 * Set authorization token for API calls
 * @param {string} token - Bearer token
 */
export const setAuthToken = (token) => {
  ghlApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

/**
 * Set location ID for API calls
 * @param {string} locationId - GHL Location ID
 */
let currentLocationId = null;
export const setLocationId = (locationId) => {
  currentLocationId = locationId;
};

/**
 * Get all custom field folders from GHL
 * @returns {Promise<Array>} Array of folders
 */
export const getFolders = async () => {
  try {
    if (!currentLocationId) {
      throw new Error('Location ID is not set');
    }

    const response = await ghlApi.get(`/locations/${currentLocationId}/customFields`);

    // Extract unique folders from custom fields
    const customFields = response.data?.customFields || [];
    const foldersMap = new Map();

    customFields.forEach(field => {
      if (field.parentId) {
        // This is a field inside a folder, the parentId is the folder
        // We need to find folder info
      }
    });

    // Get folders from the response
    // GHL returns folders as customFields with certain properties
    const folders = customFields.filter(item => !item.parentId && item.dataType === 'FOLDER');

    return folders;
  } catch (error) {
    logger.error('Error fetching folders:', error.response?.status || error.message);
    throw error;
  }
};

/**
 * Create a new folder in GHL
 * @param {string} folderName - Name of the folder to create
 * @returns {Promise<Object>} Created folder data
 */
export const createFolder = async (folderName) => {
  try {
    if (!currentLocationId) {
      throw new Error('Location ID is not set');
    }

    const response = await ghlApi.post(`/locations/${currentLocationId}/customFields`, {
      name: folderName,
      dataType: 'FOLDER',
    });

    return response.data;
  } catch (error) {
    logger.error('Error creating folder:', error.response?.status || error.message);
    throw error;
  }
};

/**
 * Get all custom fields from GHL
 * @returns {Promise<Array>} Array of custom fields
 */
export const getCustomFields = async () => {
  try {
    if (!currentLocationId) {
      throw new Error('Location ID is not set');
    }

    const response = await ghlApi.get(`/locations/${currentLocationId}/customFields`);
    return response.data?.customFields || [];
  } catch (error) {
    logger.error('Error fetching custom fields:', error.response?.status || error.message);
    throw error;
  }
};

/**
 * Create a new custom field in GHL
 * @param {string} fieldName - Name of the field
 * @param {string} folderId - ID of the parent folder
 * @returns {Promise<Object>} Created field data
 */
export const createCustomField = async (fieldName, folderId) => {
  try {
    if (!currentLocationId) {
      throw new Error('Location ID is not set');
    }

    const response = await ghlApi.post(`/locations/${currentLocationId}/customFields`, {
      name: fieldName,
      dataType: 'TEXT',
      parentId: folderId,
    });

    return response.data;
  } catch (error) {
    logger.error('Error creating custom field:', error.response?.status || error.message);
    throw error;
  }
};

/**
 * Check if a folder exists by name
 * @param {Array} folders - List of existing folders
 * @param {string} folderName - Folder name to check
 * @returns {Object|null} Folder object if exists, null otherwise
 */
export const findFolderByName = (folders, folderName) => {
  return folders.find(folder =>
    folder.name.toLowerCase().trim() === folderName.toLowerCase().trim()
  ) || null;
};

/**
 * Check if a field exists by name within a folder
 * @param {Array} fields - List of existing fields
 * @param {string} fieldName - Field name to check
 * @param {string} folderId - Parent folder ID
 * @returns {Object|null} Field object if exists, null otherwise
 */
export const findFieldByName = (fields, fieldName, folderId) => {
  return fields.find(field =>
    field.name.toLowerCase().trim() === fieldName.toLowerCase().trim() &&
    field.parentId === folderId
  ) || null;
};

export default {
  setAuthToken,
  setLocationId,
  getFolders,
  createFolder,
  getCustomFields,
  createCustomField,
  findFolderByName,
  findFieldByName,
};
